import { usePlanState } from "../../../utils/planState";
import React, { useState } from "react";
import { Button } from "../../../components/ui/Button";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { useTranslation } from "react-i18next";

import {
    useEmergencyStatus,
    useActivateEmergency,
    useDeactivateEmergency,
} from "../../../queries/emergency";

const DEFAULT_MESSAGE = "سنعود قريباً / We'll be back shortly";

const EmergencyButton = ({ icon = "pi pi-bell" }) => {
    const { t } = useTranslation();
    const { data } = useEmergencyStatus({ refetchInterval: 15000 });
    const activate = useActivateEmergency();
    const deactivate = useDeactivateEmergency();

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState(DEFAULT_MESSAGE);

    const active = !!data?.active;
    // No active package: the safe-screen switch cannot act (the API answers 402) — not offered.
    const noPlan = usePlanState().status !== 'active';

    const openDialog = () => {
        setMessage(data?.message || DEFAULT_MESSAGE);
        setOpen(true);
    };

    const onActivate = async () => {
        await activate.mutateAsync({ message: message?.trim() || DEFAULT_MESSAGE });
        setOpen(false);
    };

    const onDeactivate = async () => {
        await deactivate.mutateAsync();
    };

    const footer = (
        // Stack full-width on mobile (Activate on top), inline on larger screens.
        // Guarantees the red Activate button is always fully visible/reachable on
        // 375px and never overflows/clips the 95vw dialog; correct in RTL too.
        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2">
            <Button
                label={t("buttons.cancel")}
                className="p-button-text w-full sm:w-auto"
                onClick={() => setOpen(false)}
                disabled={activate.isLoading}
            />
            <Button
                label={activate.isLoading ? t("emergency.activating") : t("emergency.activate_emergency")}
                className="p-button-danger w-full sm:w-auto"
                icon="pi pi-bell"
                onClick={onActivate}
                disabled={activate.isLoading}
                autoFocus
            />
        </div>
    );

    if (noPlan) return null;

    return (
        <>
            <Button
                icon={icon}
                onClick={active ? onDeactivate : openDialog}
                tooltip={active
                    ? t("emergency.tooltip_active")
                    : t("emergency.tooltip_inactive")}
                tooltipOptions={{ position: "bottom" }}
                aria-label={t("emergency.aria_emergency_stop")}
                className={`p-button-rounded p-button-sm border-0 ${active
                    ? "p-button-warning animate-pulse"
                    : "p-button-danger p-button-text"}`}
                style={{ width: "2.25rem", height: "2.25rem" }}
                loading={deactivate.isLoading}
            />

            <Dialog
                visible={open}
                onHide={() => setOpen(false)}
                header={<span className="text-red-600 font-bold">🚨 {t("emergency.dialog_title")}</span>}
                style={{ width: "480px" }}
                breakpoints={{ "640px": "95vw" }}
                footer={footer}
                modal
            >
                <div className="space-y-3">
                    <p className="text-sm leading-relaxed" style={{ color: 'var(--pp-text-primary)' }}>
                        {t("emergency.dialog_warning_pre")} <strong>{t("emergency.dialog_warning_immediately")}</strong> {t("emergency.dialog_warning_post")}
                    </p>

                    <div>
                        <label htmlFor="emergency-message" className="block text-xs font-semibold uppercase mb-1" style={{ color: 'var(--pp-text-secondary)' }}>
                            {t("emergency.message_on_screens_label")}
                        </label>
                        <InputTextarea
                            id="emergency-message"
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            rows={2}
                            autoResize
                            className="w-full"
                            placeholder={DEFAULT_MESSAGE}
                        />
                    </div>

                    <div className="text-xs" style={{ color: 'var(--pp-text-secondary)' }}>
                        {t("emergency.dialog_tip")}
                    </div>
                </div>
            </Dialog>
        </>
    );
};

export default EmergencyButton;
